import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Calculator, SignOut, User, FileText, Scales, FilePdf, SpinnerGap, CaretDown, Lightning, Clock, Warning, ListChecks, ChartBar, DownloadSimple, Funnel, UsersThree, Envelope, CalendarBlank, CheckCircle, XCircle, Gear, ClockCounterClockwise, Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { PrivacyPage, CookiesPage, TermsPage, AIUsagePage, DisclaimerPage } from "./components/LegalPages";
import EscalaICAM from "./components/EscalaICAM";
import ComparativaBaremos from "./components/ComparativaBaremos";
import UserStats from "./components/UserStats";
import HojaEncargo from "./components/HojaEncargo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Force Spanish language attributes on document
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', 'es');
  document.documentElement.setAttribute('translate', 'no');
  document.documentElement.classList.add('notranslate');
}

// Axios interceptor: attach token from localStorage for cross-domain auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== AUTH CONTEXT ====================
const AuthContext = ({ children }) => {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return children;
};

// ==================== AUTH CALLBACK ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/');
        return;
      }
      
      const sessionId = sessionIdMatch[1];
      
      try {
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId
        });
        
        // Store token in localStorage for cross-domain support
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { state: { user: response.data.user } });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/');
      }
    };
    
    processAuth();
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-[#F4F4F0] flex items-center justify-center">
      <SpinnerGap size={48} className="animate-spin text-[#0A0A0A]" />
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const checkedRef = useRef(false);
  
  useEffect(() => {
    // Prevent double execution
    if (checkedRef.current) return;
    
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      checkedRef.current = true;
      return;
    }
    
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/');
      }
      checkedRef.current = true;
    };
    
    checkAuth();
  }, []); // Empty dependency array - run only once
  
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex items-center justify-center">
        <SpinnerGap size={48} className="animate-spin text-[#0A0A0A]" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children({ user, setUser });
};

// ==================== LANDING PAGE ====================
const LandingPage = () => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F4F4F0]/90 backdrop-blur-xl border-b border-[#171717] py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scales size={32} weight="regular" className="text-[#8B1A1A]" />
            <span className="font-serif text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tecnoabogado
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#funciones" className="text-sm uppercase tracking-[0.2em] text-[#525252] hover:text-[#0A0A0A] transition-colors">Funciones</a>
            <a href="/privacidad" className="text-sm uppercase tracking-[0.2em] text-[#525252] hover:text-[#0A0A0A] transition-colors">Legal</a>
          </nav>
          <button
            data-testid="login-button"
            onClick={handleLogin}
            className="bg-[#0A0A0A] text-white rounded-none px-6 py-3 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#171717] transition-colors shadow-[4px_4px_0px_0px_rgba(139,26,26,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px_rgba(139,26,26,1)]"
          >
            Acceder
          </button>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/2438213/pexels-photo-2438213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024&w=1536"
            alt="Abstract architecture"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#8B1A1A] mb-4">Herramienta de orientacion · Estimacion de horas</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#0A0A0A] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Calculadora de honorarios legales
            </h1>
            <p className="text-lg text-[#525252] mb-8 leading-relaxed max-w-lg" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Herramienta de orientacion para letrados. Una meta-ayuda para decidir tus honorarios en funcion de tus costes, experiencia y especialidad. Estima horas de dedicacion y obtiene justificaciones con IA.
            </p>
            <button
              data-testid="hero-login-button"
              onClick={handleLogin}
              className="bg-[#8B1A1A] text-white rounded-none px-8 py-4 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#701515] transition-colors shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]"
            >
              Comenzar gratis con Google
            </button>
          </div>
          <div className="hidden lg:block">
            <div className="bg-white border border-[#171717] rounded-none p-8 shadow-[8px_8px_0px_0px_rgba(139,26,26,1)]">
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4">
                  <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Procedimiento</span>
                  <span className="font-mono text-base" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>Juicio ordinario</span>
                </div>
                <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4">
                  <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Cuantia</span>
                  <span className="font-mono text-lg" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>25.000 €</span>
                </div>
                <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4">
                  <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Horas estimadas</span>
                  <span className="font-mono text-lg" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>25 h</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-sm uppercase tracking-[0.2em] text-[#0A0A0A] font-bold">Honorarios</span>
                  <span className="font-mono text-2xl font-bold text-[#8B1A1A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>4.000 €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section id="funciones" className="py-24 bg-white border-t border-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#0A0A0A] mb-16 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Funcionalidades principales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-[#F4F4F0] border border-[#171717] rounded-none p-8 hover:-translate-y-1 transition-transform duration-200 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <Calculator size={40} weight="regular" className="text-[#8B1A1A] mb-6" />
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Calculo orientativo</h3>
              <p className="text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Criterios orientativos con multiplicadores por urgencia, especialidad y complejidad.</p>
            </div>
            <div className="bg-[#F4F4F0] border border-[#171717] rounded-none p-8 hover:-translate-y-1 transition-transform duration-200 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <Clock size={40} weight="regular" className="text-[#8B1A1A] mb-6" />
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Estimacion de horas</h3>
              <p className="text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Tiempo medio estimado por tipo de procedimiento y fase procesal, basado en datos del sector.</p>
            </div>
            <div className="bg-[#F4F4F0] border border-[#171717] rounded-none p-8 hover:-translate-y-1 transition-transform duration-200 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <Lightning size={40} weight="regular" className="text-[#8B1A1A] mb-6" />
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Asistencia IA</h3>
              <p className="text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Justificacion profesional generada por inteligencia artificial para tus presupuestos.</p>
            </div>
            <div className="bg-[#F4F4F0] border border-[#171717] rounded-none p-8 hover:-translate-y-1 transition-transform duration-200 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <FilePdf size={40} weight="regular" className="text-[#8B1A1A] mb-6" />
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Exportar a PDF</h3>
              <p className="text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Descarga informes completos con desglose de fases y alertas relevantes.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-16 border-t border-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Scales size={24} weight="regular" className="text-[#8B1A1A]" />
                <span className="font-serif text-lg font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Tecnoabogado</span>
              </div>
              <p className="text-sm text-[#A3A3A3]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Jose Luis Vilaplana<br />
                Herramientas digitales para profesionales del derecho
              </p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-[0.2em] mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-[#A3A3A3]">
                <li><a href="/privacidad" className="hover:text-white transition-colors">Politica de privacidad</a></li>
                <li><a href="/cookies" className="hover:text-white transition-colors">Politica de cookies</a></li>
                <li><a href="/terminos" className="hover:text-white transition-colors">Terminos de uso</a></li>
                <li><a href="/uso-ia" className="hover:text-white transition-colors">Transparencia IA</a></li>
                <li><a href="/aviso-legal" className="hover:text-white transition-colors">Aviso legal</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-[0.2em] mb-4">Webs</h4>
              <ul className="space-y-2 text-sm text-[#A3A3A3]">
                <li><a href="https://www.iabogacia.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">www.iabogacia.com</a></li>
                <li><a href="https://www.tecnoabogado.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">www.tecnoabogado.com</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-[0.2em] mb-4">Contacto</h4>
              <p className="text-sm text-[#A3A3A3]">
                <a href="mailto:consultas@tecnoabogado.com" className="hover:text-white transition-colors">consultas@tecnoabogado.com</a>
              </p>
            </div>
          </div>
          <div className="border-t border-[#333] mt-12 pt-8 text-center text-sm text-[#A3A3A3]">
            © {new Date().getFullYear()} Jose Luis Vilaplana (Tecnoabogado). Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== DASHBOARD ====================
const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [cuantia, setCuantia] = useState('');
  const [sinCuantia, setSinCuantia] = useState(false);
  const [tipoProcedimiento, setTipoProcedimiento] = useState('civil_ordinario');
  const [baremo, setBaremo] = useState('icam');
  const [complejidad, setComplejidad] = useState('media');
  const [urgencia, setUrgencia] = useState(false);
  const [especialidad, setEspecialidad] = useState(false);
  const [fueraSede, setFueraSede] = useState(false);
  const [multiplesPartes, setMultiplesPartes] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [procedimientos, setProcedimientos] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCriterioInfo, setShowCriterioInfo] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(user?.newsletter_consent || false);
  const [historial, setHistorial] = useState([]);
  const [showHistorial, setShowHistorial] = useState(false);
  const [historialLoading, setHistorialLoading] = useState(false);
  
  const PROCEDIMIENTOS_FALLBACK = [
    { id: 'civil_ordinario', nombre: 'Juicio ordinario civil', jurisdiccion: 'civil' },
    { id: 'civil_verbal', nombre: 'Juicio verbal civil', jurisdiccion: 'civil' },
    { id: 'desahucio', nombre: 'Desahucio por falta de pago', jurisdiccion: 'civil' },
    { id: 'monitorio', nombre: 'Proceso monitorio', jurisdiccion: 'civil' },
    { id: 'ejecucion', nombre: 'Ejecucion de titulos', jurisdiccion: 'civil' },
    { id: 'familia_contencioso', nombre: 'Divorcio/separacion contencioso', jurisdiccion: 'familia' },
    { id: 'familia_mutuo_acuerdo', nombre: 'Divorcio/separacion mutuo acuerdo', jurisdiccion: 'familia' },
    { id: 'herencia', nombre: 'Division judicial de herencia', jurisdiccion: 'familia' },
    { id: 'penal_instruccion', nombre: 'Diligencias de instruccion penal', jurisdiccion: 'penal' },
    { id: 'penal_juicio_oral', nombre: 'Juicio oral penal', jurisdiccion: 'penal' },
    { id: 'penal_juicio_rapido', nombre: 'Juicio rapido', jurisdiccion: 'penal' },
    { id: 'contencioso_ordinario', nombre: 'Contencioso-administrativo ordinario', jurisdiccion: 'contencioso' },
    { id: 'contencioso_abreviado', nombre: 'Contencioso-administrativo abreviado', jurisdiccion: 'contencioso' },
    { id: 'laboral', nombre: 'Procedimiento laboral ordinario', jurisdiccion: 'laboral' },
    { id: 'laboral_despido', nombre: 'Despido', jurisdiccion: 'laboral' },
    { id: 'concurso', nombre: 'Concurso de acreedores', jurisdiccion: 'mercantil' },
    { id: 'recurso_apelacion', nombre: 'Recurso de apelacion', jurisdiccion: 'recursos' },
    { id: 'recurso_casacion', nombre: 'Recurso de casacion', jurisdiccion: 'recursos' },
  ];

  useEffect(() => {
    const fetchProcedimientos = async () => {
      try {
        const response = await axios.get(`${API}/procedimientos`);
        const procs = response.data.procedimientos || [];
        setProcedimientos(procs.length > 0 ? procs : PROCEDIMIENTOS_FALLBACK);
      } catch (error) {
        console.error('Error fetching procedimientos:', error);
        setProcedimientos(PROCEDIMIENTOS_FALLBACK);
      }
    };
    fetchProcedimientos();
    
    // Fetch newsletter consent status
    const fetchNewsletterConsent = async () => {
      try {
        const response = await axios.get(`${API}/user/newsletter-consent`);
        setNewsletterConsent(response.data.newsletter_consent);
      } catch (error) {
        console.error('Error fetching newsletter consent:', error);
      }
    };
    fetchNewsletterConsent();
  }, []);
  
  const handleNewsletterConsent = async (consent) => {
    try {
      await axios.post(`${API}/user/newsletter-consent`, { consent });
      setNewsletterConsent(consent);
    } catch (error) {
      console.error('Error updating newsletter consent:', error);
    }
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('session_token');
      navigate('/');
    }
  };
  
  const handleCalculate = async () => {
    if (!sinCuantia && (!cuantia || parseFloat(cuantia) <= 0)) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate`, {
        cuantia: sinCuantia ? 0 : parseFloat(cuantia),
        tipo_procedimiento: tipoProcedimiento,
        baremo,
        complejidad,
        urgencia,
        especialidad,
        fuera_sede: fueraSede,
        multiples_partes: multiplesPartes,
        sin_cuantia: sinCuantia
      });
      
      setResultado(response.data);
    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAIEstimate = async () => {
    if (!sinCuantia && (!cuantia || parseFloat(cuantia) <= 0)) return;
    if (!descripcion.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai-estimate`, {
        cuantia: sinCuantia ? 0 : parseFloat(cuantia),
        tipo_procedimiento: tipoProcedimiento,
        descripcion,
        baremo,
        complejidad,
        urgencia,
        especialidad,
        sin_cuantia: sinCuantia
      });
      
      setResultado(response.data);
    } catch (error) {
      console.error('AI estimation error:', error);
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleDownloadPDF = async () => {
    if (!resultado) return;
    
    setPdfLoading(true);
    try {
      const response = await axios.post(`${API}/generate-pdf`, {
        cuantia: resultado.cuantia,
        tipo_procedimiento: resultado.tipo_procedimiento,
        baremo: resultado.baremo,
        honorarios_base: resultado.honorarios_base,
        honorarios_final: resultado.honorarios_final,
        horas_estimadas: resultado.horas_estimadas ? {
          horas_estimadas: resultado.horas_estimadas,
          rango_horas: resultado.rango_horas
        } : null,
        multiplicadores_aplicados: resultado.multiplicadores_aplicados,
        fases: resultado.fases || null,
        alertas: resultado.alertas || null,
        ai_justificacion: resultado.ai_justificacion || null
      }, { 
        responseType: 'blob'
      });
      
      // Create blob with explicit type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create link and force download
      const link = document.createElement('a');
      link.href = url;
      link.download = `presupuesto_honorarios_${Date.now()}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup after delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error al generar el PDF. Por favor, intenta de nuevo.');
    } finally {
      setPdfLoading(false);
    }
  };
  
  const fetchHistorial = async () => {
    setHistorialLoading(true);
    try {
      const response = await axios.get(`${API}/history`);
      setHistorial(response.data.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistorialLoading(false);
    }
  };
  
  const deleteHistorialItem = async (historyId) => {
    try {
      await axios.delete(`${API}/history/${historyId}`);
      setHistorial(prev => prev.filter(h => h.history_id !== historyId));
    } catch (error) {
      console.error('Error deleting history item:', error);
    }
  };
  
  const clearHistorial = async () => {
    try {
      await axios.delete(`${API}/history`);
      setHistorial([]);
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };
  
  const loadFromHistory = (entry) => {
    setCuantia(String(entry.cuantia));
    setTipoProcedimiento(entry.tipo_procedimiento_id || 'civil_ordinario');
    setBaremo(entry.baremo?.includes('ICAM') ? 'icam' : 'icab');
    setComplejidad(entry.complejidad || 'media');
    setResultado(null);
    setShowHistorial(false);
  };
  
  // Fetch history when panel opens
  const toggleHistorial = () => {
    if (!showHistorial) fetchHistorial();
    setShowHistorial(!showHistorial);
  };
  
  // Also refresh history after each calculation
  const handleCalculateWithHistory = async () => {
    await handleCalculate();
    if (showHistorial) fetchHistorial();
  };
  
  const handleAIEstimateWithHistory = async () => {
    await handleAIEstimate();
    if (showHistorial) fetchHistorial();
  };
  
  // Group procedimientos by jurisdiccion
  const procedimientosPorJurisdiccion = procedimientos.reduce((acc, proc) => {
    const jur = proc.jurisdiccion || 'otros';
    if (!acc[jur]) acc[jur] = [];
    acc[jur].push(proc);
    return acc;
  }, {});
  
  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F4F4F0]/90 backdrop-blur-xl border-b border-[#171717] py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scales size={32} weight="regular" className="text-[#8B1A1A]" />
            <span className="font-serif text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tecnoabogado
            </span>
          </div>
          <div className="relative">
            <button
              data-testid="user-menu-button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 bg-white border border-[#171717] rounded-none px-4 py-2 hover:bg-[#F4F4F0] transition-colors"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <User size={24} weight="regular" className="text-[#525252]" />
              )}
              <span className="text-sm font-mono hidden sm:block" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{user?.name}</span>
              <CaretDown size={16} weight="bold" className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-[#171717] rounded-none shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
                <div className="px-4 py-3 border-b border-[#E5E5E2]">
                  <p className="text-xs text-[#525252] truncate">{user?.email}</p>
                </div>
                {user?.is_admin && (
                  <a
                    href="/admin"
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#0A0A0A] hover:bg-[#F4F4F0] transition-colors border-b border-[#E5E5E2]"
                  >
                    <Gear size={18} weight="regular" />
                    Panel de administracion
                  </a>
                )}
                <button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#8B1A1A] hover:bg-[#F4F4F0] transition-colors"
                >
                  <SignOut size={18} weight="regular" />
                  Cerrar sesion
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12 flex items-start justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Calculadora de honorarios
            </h1>
            <p className="text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Herramienta orientativa para estimar honorarios y tiempo de dedicacion. Los honorarios profesionales son libres.
            </p>
          </div>
          <button
            data-testid="toggle-history-button"
            onClick={toggleHistorial}
            className={`flex items-center gap-2 px-4 py-3 font-mono text-sm uppercase tracking-[0.1em] border transition-all shrink-0 ${
              showHistorial
                ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                : 'bg-white text-[#0A0A0A] border-[#171717] hover:bg-[#F4F4F0]'
            }`}
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            <ClockCounterClockwise size={18} weight="bold" />
            Historial
          </button>
        </div>
        
        {/* Disclaimer importante */}
        <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 mb-8">
          <p className="text-sm text-[#92400E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <strong>Aviso importante:</strong> Los honorarios profesionales de la abogacia son libres. Esta herramienta es una meta-ayuda orientativa y no puede considerarse como minimo ni como orientador obligatorio. El abogado o abogada decidira sus honorarios en funcion de sus costes, experiencia, especialidad y demas factores que considere oportunos.
          </p>
        </div>
        
        {/* Newsletter consent - desactivado temporalmente */}
        
        {/* User Stats */}
        <UserStats />
        
        {/* History Panel */}
        {showHistorial && (
          <div className="bg-white border border-[#171717] rounded-none p-6 mb-8 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]" data-testid="history-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ClockCounterClockwise size={24} weight="regular" className="text-[#8B1A1A]" />
                <h2 className="text-lg font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Historial de calculos
                </h2>
                <span className="font-mono text-xs bg-[#F4F4F0] px-2 py-1 text-[#525252]">{historial.length}</span>
              </div>
              {historial.length > 0 && (
                <button
                  data-testid="clear-history-button"
                  onClick={clearHistorial}
                  className="flex items-center gap-1 text-xs text-[#8B1A1A] hover:text-[#701515] font-mono uppercase tracking-[0.1em] transition-colors"
                >
                  <Trash size={14} weight="bold" />
                  Borrar todo
                </button>
              )}
            </div>
            
            {historialLoading ? (
              <div className="flex items-center justify-center py-8">
                <SpinnerGap size={24} className="animate-spin text-[#525252]" />
              </div>
            ) : historial.length === 0 ? (
              <p className="text-sm text-[#525252] text-center py-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Aun no hay calculos guardados. Cada calculo se guarda automaticamente.
              </p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {historial.map((entry) => (
                  <div
                    key={entry.history_id}
                    className="flex items-center justify-between p-3 border border-[#E5E5E2] hover:border-[#171717] transition-colors group"
                  >
                    <button
                      data-testid={`history-load-${entry.history_id}`}
                      onClick={() => loadFromHistory(entry)}
                      className="flex-1 flex items-start gap-4 text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-[#0A0A0A] truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            {entry.tipo_procedimiento}
                          </span>
                          {entry.tipo === 'ia' && (
                            <span className="text-[9px] font-mono uppercase bg-[#8B1A1A] text-white px-1.5 py-0.5 shrink-0">IA</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#525252]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          <span>{entry.cuantia?.toLocaleString('es-ES')} €</span>
                          <span>→</span>
                          <span className="font-semibold text-[#8B1A1A]">{entry.honorarios_final?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
                          {entry.horas_estimadas && <span>· {entry.horas_estimadas}h</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-[10px] text-[#A3A3A3] font-mono">
                          {new Date(entry.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                        </span>
                      </div>
                    </button>
                    <button
                      data-testid={`history-delete-${entry.history_id}`}
                      onClick={() => deleteHistorialItem(entry.history_id)}
                      className="ml-2 p-1 text-[#A3A3A3] hover:text-[#8B1A1A] opacity-0 group-hover:opacity-100 transition-all"
                      title="Eliminar"
                    >
                      <XCircle size={18} weight="fill" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {historial.length > 0 && (
              <p className="text-[10px] text-[#A3A3A3] mt-3 text-center font-mono">
                Haz clic en un calculo para cargar sus parametros
              </p>
            )}
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white border border-[#171717] rounded-none p-8 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
            <h2 className="text-xl font-semibold text-[#0A0A0A] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Datos del asunto
            </h2>
            
            <div className="space-y-6">
              {/* Tipo de procedimiento */}
              <div>
                <label className="block text-sm uppercase tracking-[0.2em] text-[#525252] mb-2">
                  Tipo de procedimiento
                </label>
                <div className="relative">
                  <select
                    data-testid="tipo-procedimiento-select"
                    value={tipoProcedimiento}
                    onChange={(e) => setTipoProcedimiento(e.target.value)}
                    className="w-full bg-white border border-[#171717] rounded-none focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:border-transparent p-4 pr-12 font-mono cursor-pointer"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    {Object.entries(procedimientosPorJurisdiccion).map(([jurisdiccion, procs]) => (
                      <optgroup key={jurisdiccion} label={jurisdiccion.charAt(0).toUpperCase() + jurisdiccion.slice(1)}>
                        {procs.map((proc) => (
                          <option key={proc.id} value={proc.id}>{proc.nombre}</option>
                        ))}
                      </optgroup>
                    ))}
                    <optgroup label="Otros">
                      <option value="otros">Otro procedimiento (describir)</option>
                    </optgroup>
                  </select>
                  <CaretDown size={20} weight="bold" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#525252] pointer-events-none" />
                </div>
                {tipoProcedimiento === 'otros' && (
                  <p className="text-xs text-[#8B1A1A] mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Describe tu asunto en el campo de descripcion inferior para que la IA pueda analizarlo.
                  </p>
                )}
              </div>
              
              {/* Cuantia */}
              <div>
                <label className="block text-sm uppercase tracking-[0.2em] text-[#525252] mb-2">
                  Cuantia del procedimiento (€)
                </label>
                <button
                  data-testid="sin-cuantia-toggle"
                  onClick={() => {
                    setSinCuantia(!sinCuantia);
                    if (!sinCuantia) setCuantia('');
                  }}
                  className={`mb-2 flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-[0.1em] border transition-all w-full justify-center ${
                    sinCuantia
                      ? 'bg-[#8B1A1A] text-white border-[#8B1A1A]'
                      : 'bg-white text-[#525252] border-[#E5E5E2] hover:border-[#171717] hover:text-[#0A0A0A]'
                  }`}
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {sinCuantia ? 'Cuantia indeterminada (18.000€)' : 'Sin cuantia / Indeterminada'}
                </button>
                <input
                  data-testid="cuantia-input"
                  type="number"
                  value={sinCuantia ? '' : cuantia}
                  onChange={(e) => setCuantia(e.target.value)}
                  placeholder={sinCuantia ? 'Cuantia indeterminada (18.000€)' : '25000'}
                  disabled={sinCuantia}
                  className={`w-full bg-white border border-[#171717] rounded-none focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:border-transparent p-4 font-mono ${sinCuantia ? 'opacity-40 cursor-not-allowed bg-[#F4F4F0]' : ''}`}
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                />
              </div>
              
              {/* Baremo - ahora como "Criterio" sin mencionar colegios */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm uppercase tracking-[0.2em] text-[#525252]">
                    Criterio de referencia
                  </label>
                  <button
                    data-testid="criterio-info-button"
                    onClick={() => setShowCriterioInfo(true)}
                    className="w-5 h-5 rounded-full border border-[#525252] text-[#525252] text-xs flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
                    title="Informacion sobre criterios"
                  >
                    ?
                  </button>
                </div>
                {showCriterioInfo && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCriterioInfo(false)}>
                    <div className="bg-white border border-[#171717] rounded-none p-6 max-w-lg w-full shadow-[8px_8px_0px_0px_rgba(139,26,26,1)]" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                          Criterios de referencia
                        </h3>
                        <button
                          data-testid="criterio-info-close"
                          onClick={() => setShowCriterioInfo(false)}
                          className="text-[#525252] hover:text-[#0A0A0A] text-xl leading-none font-bold"
                        >
                          ×
                        </button>
                      </div>
                      <p className="text-sm text-[#525252] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        Se ofrecen <strong>dos alternativas de calculo</strong> para que puedas comparar cual se ajusta mejor a tu asunto:
                      </p>
                      <div className="space-y-3">
                        <div className="p-3 border border-[#E5E5E2] bg-[#F4F4F0]">
                          <p className="font-semibold text-sm text-[#0A0A0A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>Criterio A</p>
                          <p className="text-sm text-[#525252] mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            Basado en escala porcentual sobre la cuantia del procedimiento. Calcula los honorarios aplicando porcentajes acumulativos por tramos.
                          </p>
                        </div>
                        <div className="p-3 border border-[#E5E5E2] bg-[#F4F4F0]">
                          <p className="font-semibold text-sm text-[#0A0A0A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>Criterio B</p>
                          <p className="text-sm text-[#525252] mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            Basado en grados de dedicacion y trabajo profesional. Evalua la complejidad y esfuerzo requerido del procedimiento.
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-[#525252] mt-4 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        Prueba ambos criterios para obtener una vision mas completa y decidir la estimacion que mejor se adapte a tu caso.
                      </p>
                      <button
                        onClick={() => setShowCriterioInfo(false)}
                        className="w-full mt-4 bg-[#0A0A0A] text-white rounded-none px-6 py-3 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#171717] transition-colors"
                      >
                        Entendido
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex gap-4">
                  <button
                    data-testid="baremo-icam-button"
                    onClick={() => setBaremo('icam')}
                    className={`flex-1 py-3 px-4 font-mono text-sm uppercase tracking-[0.1em] border transition-all ${
                      baremo === 'icam' 
                        ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' 
                        : 'bg-white text-[#0A0A0A] border-[#171717] hover:bg-[#F4F4F0]'
                    }`}
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    Criterio A
                  </button>
                  <button
                    data-testid="baremo-icab-button"
                    onClick={() => setBaremo('icab')}
                    className={`flex-1 py-3 px-4 font-mono text-sm uppercase tracking-[0.1em] border transition-all ${
                      baremo === 'icab' 
                        ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' 
                        : 'bg-white text-[#0A0A0A] border-[#171717] hover:bg-[#F4F4F0]'
                    }`}
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    Criterio B
                  </button>
                </div>
              </div>
              
              {/* Complejidad */}
              <div>
                <label className="block text-sm uppercase tracking-[0.2em] text-[#525252] mb-2">
                  Complejidad estimada
                </label>
                <div className="flex gap-2">
                  {[{id: 'baja', label: 'Baja'}, {id: 'media', label: 'Normal'}, {id: 'alta', label: 'Alta'}].map((nivel) => (
                    <button
                      key={nivel.id}
                      data-testid={`complejidad-${nivel.id}-button`}
                      onClick={() => setComplejidad(nivel.id)}
                      className={`flex-1 py-2 px-3 font-mono text-xs uppercase tracking-[0.1em] border transition-all ${
                        complejidad === nivel.id 
                          ? 'bg-[#8B1A1A] text-white border-[#8B1A1A]' 
                          : 'bg-white text-[#0A0A0A] border-[#171717] hover:bg-[#F4F4F0]'
                      }`}
                      style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                    >
                      {nivel.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Multiplicadores */}
              <div>
                <label className="block text-sm uppercase tracking-[0.2em] text-[#525252] mb-4">
                  Factores adicionales
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-[#E5E5E2] hover:border-[#171717] transition-colors">
                    <input
                      data-testid="urgencia-checkbox"
                      type="checkbox"
                      checked={urgencia}
                      onChange={(e) => setUrgencia(e.target.checked)}
                      className="w-5 h-5 accent-[#8B1A1A]"
                    />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Urgencia (x1.2)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-[#E5E5E2] hover:border-[#171717] transition-colors">
                    <input
                      data-testid="especialidad-checkbox"
                      type="checkbox"
                      checked={especialidad}
                      onChange={(e) => setEspecialidad(e.target.checked)}
                      className="w-5 h-5 accent-[#8B1A1A]"
                    />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Especialidad (x1.15)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-[#E5E5E2] hover:border-[#171717] transition-colors">
                    <input
                      data-testid="fuera-sede-checkbox"
                      type="checkbox"
                      checked={fueraSede}
                      onChange={(e) => setFueraSede(e.target.checked)}
                      className="w-5 h-5 accent-[#8B1A1A]"
                    />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Fuera sede (x1.1)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-[#E5E5E2] hover:border-[#171717] transition-colors">
                    <input
                      data-testid="multiples-partes-checkbox"
                      type="checkbox"
                      checked={multiplesPartes}
                      onChange={(e) => setMultiplesPartes(e.target.checked)}
                      className="w-5 h-5 accent-[#8B1A1A]"
                    />
                    <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Varias partes (x1.1)</span>
                  </label>
                </div>
              </div>
              
              {/* Descripcion para IA */}
              <div>
                <label className="block text-sm uppercase tracking-[0.2em] text-[#525252] mb-2">
                  Descripcion del caso <span className="normal-case tracking-normal text-[#8B1A1A]">(activa el analisis IA)</span>
                </label>
                <textarea
                  data-testid="descripcion-textarea"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Describe el asunto con el mayor detalle posible: tipo de conflicto, circunstancias relevantes, numero de partes, documentacion existente, plazos conocidos... Cuanto mas contexto aportes, mas precisa sera la justificacion generada por IA."
                  rows={4}
                  className="w-full bg-white border border-[#171717] rounded-none focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:border-transparent p-4 resize-none"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
                {!descripcion.trim() && (
                  <p className="text-xs text-[#A3A3A3] mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Rellena este campo para activar el boton "Analisis IA"
                  </p>
                )}
              </div>
              
              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  data-testid="calculate-button"
                  onClick={handleCalculateWithHistory}
                  disabled={loading || (!sinCuantia && !cuantia)}
                  className="flex-1 bg-[#0A0A0A] text-white rounded-none px-8 py-4 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#171717] transition-colors shadow-[4px_4px_0px_0px_rgba(139,26,26,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px_rgba(139,26,26,1)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <SpinnerGap size={20} className="animate-spin" /> : <Calculator size={20} weight="bold" />}
                  Calcular
                </button>
                <button
                  data-testid="ai-estimate-button"
                  onClick={handleAIEstimateWithHistory}
                  disabled={aiLoading || (!sinCuantia && !cuantia) || !descripcion.trim()}
                  className="flex-1 bg-[#8B1A1A] text-white rounded-none px-8 py-4 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#701515] transition-colors shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {aiLoading ? <SpinnerGap size={20} className="animate-spin" /> : <Lightning size={20} weight="bold" />}
                  Analisis IA
                </button>
              </div>
            </div>
          </div>
          
          {/* Results */}
          <div className="space-y-6">
            {resultado ? (
              <>
                {/* Main Result */}
                <div className="bg-white border border-[#171717] rounded-none p-8 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
                  <h2 className="text-xl font-semibold text-[#0A0A0A] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Resultado del calculo
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4">
                      <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Procedimiento</span>
                      <span className="font-mono text-base text-right max-w-[60%]" data-testid="result-tipo" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {resultado.tipo_procedimiento}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4">
                      <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Cuantia</span>
                      <span className="font-mono text-lg" data-testid="result-cuantia" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {resultado.sin_cuantia 
                          ? <span className="text-[#8B1A1A]">Indeterminada (18.000 €)</span>
                          : `${resultado.cuantia?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`
                        }
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4">
                      <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Criterio aplicado</span>
                      <span className="font-mono text-base" data-testid="result-baremo" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {resultado.baremo?.includes('ICAM') ? 'Criterio A' : 'Criterio B'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4">
                      <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Honorarios base</span>
                      <span className="font-mono text-lg" data-testid="result-base" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {resultado.honorarios_base?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                      </span>
                    </div>
                    
                    {resultado.nota && (
                      <div className="border-b border-[#E5E5E2] pb-4 bg-[#F8F6F0] -mx-8 px-8 py-3" data-testid="result-nota-escala">
                        <span className="text-xs uppercase tracking-[0.15em] text-[#8B1A1A] font-semibold block mb-1">Criterio aplicado</span>
                        <p className="text-sm text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {resultado.nota}
                        </p>
                      </div>
                    )}
                    
                    {resultado.porcentaje_escala && resultado.porcentaje_escala < 1.0 && (
                      <div className="flex justify-between items-center border-b border-[#E5E5E2] pb-4" data-testid="result-porcentaje-escala">
                        <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">Porcentaje escala</span>
                        <span className="font-mono text-base text-[#8B1A1A] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {Math.round(resultado.porcentaje_escala * 100)}%
                        </span>
                      </div>
                    )}
                    
                    {resultado.multiplicadores_aplicados?.length > 0 && (
                      <div className="border-b border-[#E5E5E2] pb-4">
                        <span className="text-sm uppercase tracking-[0.2em] text-[#525252] block mb-2">Multiplicadores</span>
                        <ul className="space-y-1">
                          {resultado.multiplicadores_aplicados.map((mult, idx) => (
                            <li key={idx} className="font-mono text-sm text-[#525252]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                              • {mult}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center pt-2 bg-[#F4F4F0] -mx-8 px-8 py-4 mt-4">
                      <span className="text-sm uppercase tracking-[0.2em] text-[#0A0A0A] font-bold">Honorarios finales</span>
                      <span className="font-mono text-2xl font-bold text-[#8B1A1A]" data-testid="result-final" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {resultado.honorarios_final?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                      </span>
                    </div>
                  </div>
                  
                  <button
                    data-testid="download-pdf-button"
                    onClick={handleDownloadPDF}
                    disabled={pdfLoading}
                    className="w-full mt-6 bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] rounded-none px-8 py-4 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#0A0A0A] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {pdfLoading ? <SpinnerGap size={20} className="animate-spin" /> : <FilePdf size={20} weight="bold" />}
                    Descargar PDF
                  </button>
                  
                  <div className="mt-3">
                    <HojaEncargo resultado={resultado} cuantia={parseFloat(cuantia)} />
                  </div>
                </div>
                
                {/* Time Estimation */}
                {resultado.horas_estimadas && (
                  <div className="bg-white border border-[#171717] rounded-none p-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
                    <div className="flex items-center gap-3 mb-4">
                      <Clock size={24} weight="regular" className="text-[#8B1A1A]" />
                      <h3 className="text-lg font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Estimacion de tiempo
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-[#F4F4F0] p-4 text-center">
                        <span className="font-mono text-2xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {resultado.horas_estimadas}
                        </span>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#525252] mt-1">Horas estimadas</p>
                      </div>
                      <div className="bg-[#F4F4F0] p-4 text-center">
                        <span className="font-mono text-lg text-[#525252]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {resultado.rango_horas?.min} - {resultado.rango_horas?.max}
                        </span>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#525252] mt-1">Rango (h)</p>
                      </div>
                    </div>
                    {resultado.coste_por_horas && (
                      <p className="text-sm text-[#525252] border-t border-[#E5E5E2] pt-3">
                        Coste estimado por horas: <span className="font-mono font-semibold">{resultado.coste_por_horas?.toLocaleString('es-ES')} €</span> (a {resultado.tarifa_hora_referencia}€/h)
                      </p>
                    )}
                  </div>
                )}
                
                {/* Phases */}
                {resultado.fases && resultado.fases.length > 0 && (
                  <div className="bg-white border border-[#171717] rounded-none p-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
                    <div className="flex items-center gap-3 mb-4">
                      <ListChecks size={24} weight="regular" className="text-[#8B1A1A]" />
                      <h3 className="text-lg font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Fases del procedimiento
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {resultado.fases.map((fase, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm text-[#0A0A0A]">{fase.nombre}</span>
                              <span className="font-mono text-xs text-[#525252]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                                {fase.horas.min}-{fase.horas.max}h
                              </span>
                            </div>
                            <div className="h-2 bg-[#E5E5E2] rounded-none overflow-hidden">
                              <div 
                                className="h-full bg-[#8B1A1A]" 
                                style={{ width: `${fase.porcentaje}%` }}
                              />
                            </div>
                          </div>
                          <span className="font-mono text-sm w-12 text-right" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                            {fase.porcentaje}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Alerts */}
                {resultado.alertas && resultado.alertas.length > 0 && (
                  <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] rounded-none p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Warning size={24} weight="fill" className="text-[#F59E0B]" />
                      <h3 className="text-lg font-semibold text-[#92400E]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Alertas y consideraciones
                      </h3>
                    </div>
                    <ul className="space-y-2">
                      {resultado.alertas.map((alerta, idx) => (
                        <li key={idx} className="text-sm text-[#92400E] flex items-start gap-2">
                          <span className="text-[#F59E0B]">•</span>
                          {alerta}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Market Comparison */}
                {resultado.mercado && (
                  <div className="bg-white border border-[#171717] rounded-none p-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]" data-testid="result-market-comparison">
                    <div className="flex items-center gap-3 mb-4">
                      <ChartBar size={24} weight="regular" className="text-[#8B1A1A]" />
                      <h3 className="text-lg font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Comparativa de mercado
                      </h3>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-[#F4F4F0] p-4 text-center">
                        <span className="font-mono text-lg font-bold text-[#525252]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {resultado.mercado.low_cost?.toLocaleString('es-ES')} €
                        </span>
                        <p className="text-xs uppercase tracking-[0.15em] text-[#525252] mt-1">Low cost</p>
                      </div>
                      <div className="bg-[#F4F4F0] p-4 text-center border-2 border-[#8B1A1A]">
                        <span className="font-mono text-lg font-bold text-[#8B1A1A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {resultado.mercado.asesoria?.toLocaleString('es-ES')} €
                        </span>
                        <p className="text-xs uppercase tracking-[0.15em] text-[#8B1A1A] mt-1">Asesoria</p>
                      </div>
                      <div className="bg-[#F4F4F0] p-4 text-center">
                        <span className="font-mono text-lg font-bold text-[#525252]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {resultado.mercado.bufete?.toLocaleString('es-ES')} €
                        </span>
                        <p className="text-xs uppercase tracking-[0.15em] text-[#525252] mt-1">Bufete</p>
                      </div>
                    </div>
                    {resultado.nota_escala && (
                      <p className="text-xs text-[#525252] mt-3 border-t border-[#E5E5E2] pt-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {resultado.nota_escala}
                      </p>
                    )}
                    {resultado.grado_icab && (
                      <p className="text-xs text-[#525252] mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        Grado ICAB: {resultado.grado_icab} · Valor referencia ICAM: {resultado.valor_referencia_icam?.toLocaleString('es-ES')} €
                      </p>
                    )}
                  </div>
                )}
                
                {/* Comparativa de baremos */}
                <ComparativaBaremos cuantia={resultado.cuantia} tipoProcedimiento={tipoProcedimiento} />
                
                {/* Tabla escala ICAM */}
                <EscalaICAM cuantia={resultado.cuantia} />
                
                {/* AI Justification */}
                {resultado.ai_justificacion && (
                  <div className="relative bg-[#F4F4F0] border-2 border-[#8B1A1A] rounded-none p-8 shadow-[6px_6px_0px_0px_rgba(139,26,26,1)]">
                    <div className="flex items-center gap-3 mb-4">
                      <Lightning size={24} weight="fill" className="text-[#8B1A1A]" />
                      <h3 className="text-lg font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Justificacion generada por IA
                      </h3>
                    </div>
                    <p className="text-[#525252] whitespace-pre-wrap" data-testid="ai-justification" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {resultado.ai_justificacion}
                    </p>
                  </div>
                )}
                
                {/* Nuevo calculo button */}
                <button
                  data-testid="nuevo-calculo-button"
                  onClick={() => {
                    setResultado(null);
                    setCuantia('');
                    setSinCuantia(false);
                    setTipoProcedimiento('civil_ordinario');
                    setBaremo('icam');
                    setComplejidad('media');
                    setUrgencia(false);
                    setEspecialidad(false);
                    setFueraSede(false);
                    setMultiplesPartes(false);
                    setDescripcion('');
                  }}
                  className="w-full bg-[#0A0A0A] text-white rounded-none px-8 py-4 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#171717] transition-colors shadow-[4px_4px_0px_0px_rgba(139,26,26,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px_rgba(139,26,26,1)] flex items-center justify-center gap-2"
                >
                  <Calculator size={20} weight="bold" />
                  Nuevo calculo
                </button>
              </>
            ) : (
              <div className="bg-white border border-[#E5E5E2] rounded-none p-12 flex flex-col items-center justify-center text-center">
                <FileText size={64} weight="thin" className="text-[#E5E5E2] mb-4" />
                <p className="text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Selecciona el tipo de procedimiento, introduce la cuantia y haz clic en "Calcular" para obtener el presupuesto orientativo de honorarios.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Sugerencias */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="bg-white border border-[#E5E5E2] rounded-none p-6 text-center">
          <p className="text-sm text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <strong className="text-[#0A0A0A]">¿Echas en falta algun procedimiento o quieres sugerir mejoras?</strong>
            <br />
            Escribenos a{' '}
            <a 
              href="mailto:consultas@tecnoabogado.com?subject=Sugerencia%20Calculadora%20Honorarios" 
              className="text-[#8B1A1A] underline hover:text-[#701515] transition-colors font-semibold"
              data-testid="suggestions-email-link"
            >
              consultas@tecnoabogado.com
            </a>
            {' '}y lo tendremos en cuenta.
          </p>
        </div>
      </div>
      
      {/* Footer Links */}
      <footer className="border-t border-[#171717] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-6 justify-center text-sm text-[#525252] mb-4">
            <a href="/privacidad" className="hover:text-[#0A0A0A] transition-colors">Privacidad</a>
            <a href="/cookies" className="hover:text-[#0A0A0A] transition-colors">Cookies</a>
            <a href="/terminos" className="hover:text-[#0A0A0A] transition-colors">Terminos</a>
            <a href="/uso-ia" className="hover:text-[#0A0A0A] transition-colors">Transparencia IA</a>
            <a href="/aviso-legal" className="hover:text-[#0A0A0A] transition-colors">Aviso legal</a>
          </div>
          <div className="flex flex-wrap gap-6 justify-center text-sm text-[#525252]">
            <a href="https://www.iabogacia.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#0A0A0A] transition-colors">www.iabogacia.com</a>
            <a href="https://www.tecnoabogado.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#0A0A0A] transition-colors">www.tecnoabogado.com</a>
            <a href="mailto:consultas@tecnoabogado.com" className="hover:text-[#0A0A0A] transition-colors">consultas@tecnoabogado.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== ADMIN PAGE ====================
const AdminPage = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [soloNewsletter, setSoloNewsletter] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/users?`;
      if (fechaDesde) url += `fecha_desde=${fechaDesde}&`;
      if (fechaHasta) url += `fecha_hasta=${fechaHasta}&`;
      if (soloNewsletter) url += `solo_newsletter=true&`;
      
      const response = await axios.get(url);
      setUsers(response.data.users || []);
      setStats(response.data.stats);
    } catch (error) {
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const handleFilter = () => {
    fetchUsers();
  };
  
  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      let url = `${API}/admin/export-csv?`;
      if (soloNewsletter) url += `solo_newsletter=true`;
      
      const response = await axios.get(url, { 
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `usuarios_tecnoabogado_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setExportLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) {
      localStorage.removeItem('session_token');
      navigate('/');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F4F4F0]/90 backdrop-blur-xl border-b border-[#171717] py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scales size={32} weight="regular" className="text-[#8B1A1A]" />
            <span className="font-serif text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tecnoabogado
            </span>
            <span className="bg-[#8B1A1A] text-white text-xs px-2 py-1 rounded-none font-mono uppercase">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/dashboard" className="text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors">
              Calculadora
            </a>
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 bg-white border border-[#171717] rounded-none px-4 py-2 hover:bg-[#F4F4F0] transition-colors"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User size={24} weight="regular" className="text-[#525252]" />
                )}
                <span className="text-sm font-mono hidden sm:block" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{user?.name}</span>
                <CaretDown size={16} weight="bold" className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-[#171717] rounded-none shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#8B1A1A] hover:bg-[#F4F4F0] transition-colors"
                  >
                    <SignOut size={18} weight="regular" />
                    Cerrar sesion
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Panel de administracion
          </h1>
          <p className="text-[#525252]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Gestion de usuarios y suscripciones al newsletter.
          </p>
        </div>
        
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-[#171717] rounded-none p-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <UsersThree size={32} className="text-[#8B1A1A] mb-2" />
              <p className="font-mono text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{stats.total_users}</p>
              <p className="text-sm text-[#525252] uppercase tracking-wider">Total usuarios</p>
            </div>
            <div className="bg-white border border-[#171717] rounded-none p-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <Envelope size={32} className="text-[#8B1A1A] mb-2" />
              <p className="font-mono text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{stats.newsletter_subscribers}</p>
              <p className="text-sm text-[#525252] uppercase tracking-wider">Newsletter</p>
            </div>
            <div className="bg-white border border-[#171717] rounded-none p-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <CalendarBlank size={32} className="text-[#8B1A1A] mb-2" />
              <p className="font-mono text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{stats.users_last_7_days || 0}</p>
              <p className="text-sm text-[#525252] uppercase tracking-wider">Ultimos 7 dias</p>
            </div>
            <div className="bg-white border border-[#171717] rounded-none p-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
              <ChartBar size={32} className="text-[#8B1A1A] mb-2" />
              <p className="font-mono text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{stats.users_last_30_days || 0}</p>
              <p className="text-sm text-[#525252] uppercase tracking-wider">Ultimos 30 dias</p>
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className="bg-white border border-[#171717] rounded-none p-6 mb-6 shadow-[4px_4px_0px_0px_rgba(23,23,23,1)]">
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={20} className="text-[#8B1A1A]" />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>Filtros</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm uppercase tracking-[0.2em] text-[#525252] mb-2">Desde</label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full bg-white border border-[#171717] rounded-none p-3 font-mono"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-[0.2em] text-[#525252] mb-2">Hasta</label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full bg-white border border-[#171717] rounded-none p-3 font-mono"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer p-3 border border-[#E5E5E2] hover:border-[#171717] transition-colors w-full">
                <input
                  type="checkbox"
                  checked={soloNewsletter}
                  onChange={(e) => setSoloNewsletter(e.target.checked)}
                  className="w-5 h-5 accent-[#8B1A1A]"
                />
                <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Solo newsletter</span>
              </label>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleFilter}
                className="flex-1 bg-[#0A0A0A] text-white rounded-none px-4 py-3 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#171717] transition-colors"
              >
                Filtrar
              </button>
              <button
                onClick={handleExportCSV}
                disabled={exportLoading}
                className="bg-[#8B1A1A] text-white rounded-none px-4 py-3 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#701515] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {exportLoading ? <SpinnerGap size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
                CSV
              </button>
            </div>
          </div>
        </div>
        
        {/* Users Table */}
        <div className="bg-white border border-[#171717] rounded-none shadow-[4px_4px_0px_0px_rgba(23,23,23,1)] overflow-hidden">
          <div className="p-6 border-b border-[#171717]">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Usuarios registrados ({users.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-12 flex justify-center">
              <SpinnerGap size={48} className="animate-spin text-[#8B1A1A]" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-[#525252]">
              No se encontraron usuarios con los filtros aplicados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F4F4F0] border-b border-[#171717]">
                  <tr>
                    <th className="text-left p-4 text-sm uppercase tracking-[0.2em] text-[#525252] font-normal">Email</th>
                    <th className="text-left p-4 text-sm uppercase tracking-[0.2em] text-[#525252] font-normal">Nombre</th>
                    <th className="text-left p-4 text-sm uppercase tracking-[0.2em] text-[#525252] font-normal">Fecha registro</th>
                    <th className="text-center p-4 text-sm uppercase tracking-[0.2em] text-[#525252] font-normal">Newsletter</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={u.user_id || idx} className="border-b border-[#E5E5E2] hover:bg-[#F4F4F0] transition-colors">
                      <td className="p-4">
                        <span className="font-mono text-sm" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{u.email}</span>
                      </td>
                      <td className="p-4 text-[#525252]">{u.name}</td>
                      <td className="p-4 font-mono text-sm text-[#525252]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {formatDate(u.created_at)}
                      </td>
                      <td className="p-4 text-center">
                        {u.newsletter_consent ? (
                          <CheckCircle size={24} weight="fill" className="text-green-600 inline" />
                        ) : (
                          <XCircle size={24} weight="fill" className="text-[#E5E5E2] inline" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-[#171717] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-[#525252]">
          Panel de administracion - Tecnoabogado
        </div>
      </footer>
    </div>
  );
};

// ==================== MAIN APP ====================
function AppRouter() {
  return (
    <AuthContext>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            {(props) => <Dashboard {...props} />}
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            {(props) => <AdminPage {...props} />}
          </ProtectedRoute>
        } />
        <Route path="/privacidad" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/terminos" element={<TermsPage />} />
        <Route path="/uso-ia" element={<AIUsagePage />} />
        <Route path="/aviso-legal" element={<DisclaimerPage />} />
      </Routes>
    </AuthContext>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
